import { C } from '../../../lib/theme';
import { styles } from '../../../lib/styles';
import type { Case, CaseTrust, CustomerSuppliedTrust } from '../../../domain/initial-cases';
import { normalizeTrust } from '../../../domain/factories';
import { useAppData } from '../../../state/contexts/AppDataContext';
import { SectionEvidenceAttachments } from '../../../components/EvidenceAttachments';

// #545 — WHERE THE APPLICANT'S OWN ANSWER IS SHOWN NEXT TO THE ANALYST'S.
//
// Everything on this tab except the TCPA/TSR assessment now has an intake path,
// so most of these controls arrive PRE-FILLED with what the registrant said
// about themselves. That makes an analyst's edit a different kind of act than it
// was: before, they were recording their own finding into a blank field; now
// they may be overwriting the registrant's answer, and the file has to be able
// to say which happened.
//
// So `trust.customerSupplied` is written once at promote (`mapIntakeToTrust` in
// apps/api/src/routes/intake.ts) and never edited, and every field that has one
// carries a line underneath saying either "the applicant said this" or "the
// applicant said something else, and here it is". A silent overwrite would make
// the compliance record WORSE than leaving these fields staff-only, because a
// reader would take the analyst's text for the registrant's own words.
//
// It is per-field, not per-tab, because that is how it is read: the question is
// never "did anyone change anything", it is "who asserted THIS".
function SuppliedNote({
  field,
  supplied,
  current,
}: {
  field: keyof CustomerSuppliedTrust;
  supplied: CustomerSuppliedTrust;
  current: CaseTrust;
}) {
  // Only the keys the applicant ACTUALLY supplied. An absent key means they
  // were never asked (a pre-#545 case, or one where they skipped the section),
  // which is not the same as their having answered — see the note on
  // `mapIntakeToTrust`. Rendering a note for it would invent an assertion.
  if (!Object.prototype.hasOwnProperty.call(supplied, field)) return null;
  const was = supplied[field];
  const now = current[field];
  // Booleans read as an answer, not as a value: "false" under a tickbox is
  // ambiguous about whether it is the applicant's answer or the default.
  const render = (v: unknown): string =>
    typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v ?? '').trim() || '(blank)';
  if (was === now) {
    return (
      <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
        Supplied by the applicant.
      </div>
    );
  }
  return (
    <div style={{ fontSize: 11, color: C.warn, marginTop: 4 }}>
      Changed by 382 — the applicant supplied: <strong>{render(was)}</strong>
    </div>
  );
}

export function TrustTab({ c }: { c: Case }) {
  const { updateCase } = useAppData();
  // #545 — NORMALISE BEFORE RENDERING, and normalise before writing.
  //
  // `c.trust` is `Partial<CaseTrust>` because the column's default really is
  // `'{}'::jsonb` and `createCase` never sends the key, so that is what a case
  // created in this SPA carries. Rendering it raw made every control here an
  // UNCONTROLLED input (React reads `value={undefined}` / `checked={undefined}`
  // that way) and made `{ ...c.trust, consent }` write a one-key object back
  // over a nine-key shape. Neither showed up as an error: React's own `value`
  // and `checked` props are optional, so `string | undefined` satisfies them.
  //
  // The `satisfies CaseTrust` on the writer below is what makes the compiler
  // hold this — spreading `c.trust` there instead of `t` is a type error, which
  // is the one guard that cannot rot. TrustTab.test.tsx holds the rendering half.
  const t = normalizeTrust(c.trust);
  const supplied = t.customerSupplied ?? {};
  const patch = (changes: Partial<CaseTrust>) =>
    updateCase(c.id, { trust: { ...t, ...changes } satisfies CaseTrust });
  // #698 — "has the analyst recorded a TCPA/TSR finding at all", not "is the
  // finding a good one". Read off the value rather than a separate flag so it
  // cannot fall out of step with what the select shows; `!!` rather than
  // `!== ''` because `normalizeTrust` spreads the raw column over the empty
  // shape, so an explicit `tcpa: null` in the JSONB survives as null.
  const tcpaAssessed = !!t.tcpa;
  const note = (field: keyof CustomerSuppliedTrust) => (
    <SuppliedNote field={field} supplied={supplied} current={t} />
  );
  return (
    <div style={styles.card}>
      <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Trust Criteria Assessment</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          {/* No provenance note: the TCPA/TSR assessment is the ANALYST's own
              finding and has no intake path, deliberately (#545). A registrant's
              self-assessment of their TCPA compliance is not evidence of it.
              #698 — and that is exactly why this one field needs a state of its
              own. Every other control on this tab arrives pre-filled, so a blank
              one is visibly blank; this one starts at "— Select —" whether the
              analyst has weighed the registrant's posture and not yet recorded
              it, or never looked. The box is RED until an option is chosen and
              GREEN after.
              The colour means ASSESSED, not PASSED. Non-Compliant greens it too,
              because the finding HAS been made and the outcome is already legible
              in the select's own text. Colouring by outcome instead would say the
              analyst's correct finding of non-compliance is a fault in the form,
              and would leave the actual gap here — nobody has looked — wearing
              the same green as a clean result. */}
          <div
            data-testid="tcpa-assessment-box"
            style={{
              // Longhand, not the `border` shorthand: the palette lives in
              // global.css since #399 and these are `var()` references, which a
              // shorthand does not read back for a test to assert on.
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: tcpaAssessed ? C.success : C.danger,
              background: tcpaAssessed ? C.successBg : C.dangerBg,
              borderRadius: 8,
              padding: 12,
            }}
          >
            <label style={{ fontSize: 11, color: C.textMuted }}>TCPA/TSR Compliance Assessment</label>
            {/* Not colour-only: the same state reaches a screen reader through
                aria-invalid and is spelled out in words in the status line. */}
            <select aria-invalid={!tcpaAssessed} style={{ ...styles.select, width: '100%' }} value={t.tcpa || ''} onChange={e => patch({ tcpa: e.target.value })}>
              <option value="">— Select —</option>
              <option value="compliant">Compliant</option>
              <option value="concerns">Concerns Identified</option>
              <option value="non-compliant">Non-Compliant</option>
            </select>
            <div data-testid="tcpa-assessment-status" style={{ fontSize: 11, marginTop: 6, fontWeight: 600, color: tcpaAssessed ? C.success : C.danger }}>
              {tcpaAssessed ? 'Assessed' : 'Assessment required'}
            </div>
          </div>
        </div>
        <div>
          <div style={styles.checkItem}>
            <input type="checkbox" checked={t.autodialer} onChange={e => patch({ autodialer: e.target.checked })} />
            <span>Engages in telemarketing or auto-dialing</span>
          </div>
          {note('autodialer')}
        </div>
        <div><label style={{ fontSize: 11, color: C.textMuted }}>Consent Process</label><textarea style={styles.textarea} value={t.consent} onChange={e => patch({ consent: e.target.value })} placeholder="How does registrant obtain consent?" />{note('consent')}</div>
        <div><label style={{ fontSize: 11, color: C.textMuted }}>Opt-Out Process</label><textarea style={styles.textarea} value={t.optOut} onChange={e => patch({ optOut: e.target.value })} placeholder="How can consumers opt out?" />{note('optOut')}</div>
        <div><div style={styles.checkItem}><input type="checkbox" checked={t.dncScrub} onChange={e => patch({ dncScrub: e.target.checked })} /><span>Scrubs against DNC lists</span></div>{note('dncScrub')}</div>
        <div><div style={styles.checkItem}><input type="checkbox" checked={t.reassignedDb} onChange={e => patch({ reassignedDb: e.target.checked })} /><span>Scrubs against Reassigned Number Database</span></div>{note('reassignedDb')}</div>
        <div><label style={{ fontSize: 11, color: C.textMuted }}>Consumer Complaint Email</label><input style={styles.input} value={t.complaintEmail} onChange={e => patch({ complaintEmail: e.target.value })} />{note('complaintEmail')}</div>
        <div><label style={{ fontSize: 11, color: C.textMuted }}>Consumer Complaint Phone</label><input style={styles.input} value={t.complaintPhone} onChange={e => patch({ complaintPhone: e.target.value })} />{note('complaintPhone')}</div>
        <div style={{ gridColumn: '1/3' }}><label style={{ fontSize: 11, color: C.textMuted }}>Privacy Policy URL</label><input style={styles.input} value={t.privacyUrl} onChange={e => patch({ privacyUrl: e.target.value })} />{note('privacyUrl')}</div>
      </div>
      {/* #774 — consent-flow and opt-out screenshots, policy PDFs. */}
      <SectionEvidenceAttachments caseId={c.id} section="trust" />
    </div>
  );
}
